import { useCallback, useEffect, useState } from 'react';
import { fetchConversationState } from '../api/conversationState';
import type { Blocker, ConversationState, DecidedField } from '../types/conversationState';

export type UseConversationStateResult = {
  blockers: Blocker[];
  decidedFields: DecidedField[];
  searchTargets: string[];
  readyToSearch: boolean;
  hasConversationState: boolean;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

const EMPTY_RESULT = {
  blockers: [],
  decidedFields: [],
  searchTargets: [],
  readyToSearch: false,
  hasConversationState: false
};

export const useConversationState = (tripId: string): UseConversationStateResult => {
  const [state, setState] = useState<ConversationState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!tripId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const next = await fetchConversationState(tripId);
      setState(next);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useConversationState] Failed to fetch:', e);
    } finally {
      setIsLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    load();
  }, [load]);

  const refetch = useCallback(async () => {
    if (!tripId) {
      return;
    }
    try {
      const next = await fetchConversationState(tripId);
      setState(next);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useConversationState] Failed to refetch:', e);
    }
  }, [tripId]);

  if (state == null || state.updatedAt === null) {
    return { ...EMPTY_RESULT, isLoading, refetch };
  }

  return {
    blockers: state.blockers,
    decidedFields: state.decidedFields,
    searchTargets: state.searchTargets,
    readyToSearch: state.readyToSearch,
    hasConversationState: true,
    isLoading,
    refetch
  };
};
